<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue'
import { ChevronLeft, ChevronRight, Inbox } from 'lucide-vue-next'
import { useListState } from '@/utils/useListState'

export interface TableHeader {
  label: string
  key: string
  width?: string
  align?: 'left' | 'center' | 'right'
  sortKey?: string | null
  // 화면에 보이는 값이 item[sortKey]와 다를 때(예: 상태를 시간에 따라 바꿔 보여 주는 칸) 정렬에 쓸 값.
  // 정렬할 때마다 호출하므로 현재 시각 기준 값을 돌려줘도 된다.
  sortValue?: (item: any) => string | number
}

const props = withDefaults(defineProps<{
  headers: TableHeader[]
  items: any[]
  pageSize?: number
  emptyMessage?: string
  emptyDescription?: string
  // 지정하면 현재 페이지·정렬을 저장해 두었다가 뒤로 가기로 돌아왔을 때 복원 (화면 안에서 고유한 이름)
  stateKey?: string
}>(), {
  pageSize: 8,
  emptyMessage: '데이터가 없습니다',
  emptyDescription: '표시할 데이터가 존재하지 않습니다.',
})

const emit = defineEmits<{
  (e: 'row-click', item: any): void
}>()

// @row-click이 연결된(행 클릭으로 이동하는) 테이블에서만 행에 손가락 포인터·hover 배경을 준다.
// (다운로드 이력처럼 클릭 동작이 없는 테이블에서 포인터가 보이지 않도록)
const instance = getCurrentInstance()
const isRowClickable = computed(() => !!instance?.vnode.props?.onRowClick)

// ─── State ────────────────────────────────────────────────────────────────────
const pinnedPage = ref(1)
const sortKey = ref<string | null>(null)
const sortDir = ref<'asc' | 'desc'>('asc')

// 뒤로 가기 복원: 목록 데이터가 나중에 들어오는 화면이 많아, 복원할 페이지는 데이터가 들어온 뒤 적용한다
let pendingPage: number | null = null
if (props.stateKey) {
  const { restored } = useListState(`table:${props.stateKey}`, { page: pinnedPage, sortKey, sortDir })
  if (typeof restored?.page === 'number') pendingPage = restored.page
}

function applyPendingPage(): boolean {
  if (pendingPage === null || props.items.length === 0) return false
  // 목록이 줄어든 경우 마지막 페이지로 맞춤
  pinnedPage.value = Math.min(pendingPage, Math.max(1, Math.ceil(props.items.length / props.pageSize)))
  pendingPage = null
  return true
}
applyPendingPage()

// Reset page to 1 when items prop changes (e.g. search query changes)
watch(() => props.items, () => {
  if (applyPendingPage()) return
  pinnedPage.value = 1
}, { deep: true })

// ─── Computed ─────────────────────────────────────────────────────────────────
const sortedItems = computed(() => {
  const data = props.items
  if (!sortKey.value) return data
  const key = sortKey.value
  const sortValue = props.headers.find((h) => h.sortKey === key)?.sortValue
  return [...data].sort((a, b) => {
    let av: any = ''
    let bv: any = ''

    if (sortValue) {
      av = sortValue(a)
      bv = sortValue(b)
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir.value === 'asc' ? av - bv : bv - av
      }
    // 데이터셋은 project/session 객체 안에 이름이 있고, 다운로드 요청 등은 projectName/sessionName 필드에 바로 있다
    } else if (key === 'projectName') {
      av = a.project?.name ?? a.projectName ?? ''
      bv = b.project?.name ?? b.projectName ?? ''
    } else if (key === 'sessionName') {
      av = a.session?.name ?? a.sessionName ?? ''
      bv = b.session?.name ?? b.sessionName ?? ''
    } else if (key === 'materials') {
      av = a.materials?.join(', ') ?? ''
      bv = b.materials?.join(', ') ?? ''
    } else if (key === 'detectors') {
      av = a.detectors?.join(', ') ?? ''
      bv = b.detectors?.join(', ') ?? ''
    } else if (key === 'modalities') {
      av = a.modalities?.join(', ') ?? ''
      bv = b.modalities?.join(', ') ?? ''
    } else {
      av = (a as unknown as Record<string, unknown>)[key] ?? ''
      bv = (b as unknown as Record<string, unknown>)[key] ?? ''
    }

    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true })
    return sortDir.value === 'asc' ? cmp : -cmp
  })
})

const listStart = computed(() => (pinnedPage.value - 1) * props.pageSize)
const pagedPinned = computed(() => sortedItems.value.slice(listStart.value, listStart.value + props.pageSize))
const totalPages = computed(() => Math.ceil(props.items.length / props.pageSize))

const paginationPages = computed((): (number | '...')[] => {
  const pages: (number | '...')[] = []
  const cur = pinnedPage.value
  const total = totalPages.value
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || Math.abs(i - cur) <= 1) {
      pages.push(i)
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...')
    }
  }
  return pages
})

// ─── Handlers ────────────────────────────────────────────────────────────────
function handleSort(key: string) {
  if (sortKey.value === key) {
    if (sortDir.value === 'asc') {
      sortDir.value = 'desc'
    } else {
      sortKey.value = null
      sortDir.value = 'asc'
    }
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
  pinnedPage.value = 1
}

function handleRowClick(item: any) {
  emit('row-click', item)
}

function handlePageClick(p: number | '...') {
  if (typeof p === 'number') {
    pinnedPage.value = p
  }
}
</script>

<template>
  <div>
    <div class="table-container">
      <table class="custom-table">
        <colgroup>
          <col v-for="c in headers" :key="c.key" :style="{ width: c.width === 'auto' ? undefined : c.width }" />
        </colgroup>
        <thead>
          <tr class="header-row">
            <th
              v-for="c in headers"
              :key="c.key"
              @click="() => c.sortKey && handleSort(c.sortKey)"
              class="table-th"
              :class="{ active: sortKey && sortKey === c.sortKey }"
              :style="{
                textAlign: c.align || 'left',
                cursor: c.sortKey ? 'pointer' : 'default',
              }"
            >
              <span v-if="c.label" class="th-label-wrapper">
                {{ c.label }}
                <span v-if="c.sortKey" class="sort-icon-wrapper" :style="{ opacity: sortKey === c.sortKey ? 1 : 0.35 }">
                  <svg width="6" height="4" viewBox="0 0 6 4" fill="none"><path d="M3 0L6 4H0L3 0Z" :fill="sortKey === c.sortKey && sortDir === 'asc' ? '#6366F1' : '#9CA3AF'" /></svg>
                  <svg width="6" height="4" viewBox="0 0 6 4" fill="none"><path d="M3 4L0 0H6L3 4Z" :fill="sortKey === c.sortKey && sortDir === 'desc' ? '#6366F1' : '#9CA3AF'" /></svg>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Empty State -->
          <tr v-if="items.length === 0">
            <td :colspan="headers.length" class="empty-state-td">
              <div class="empty-state-wrapper">
                <div class="empty-state-icon">
                  <Inbox :size="22" :stroke-width="1.6" />
                </div>
                <p class="empty-state-title">{{ emptyMessage }}</p>
                <p class="empty-state-desc">{{ emptyDescription }}</p>
              </div>
            </td>
          </tr>
          <!-- Data rows -->
          <tr
            v-for="(item, idx) in pagedPinned"
            :key="item.id || idx"
            @click="handleRowClick(item)"
            class="data-row"
            :class="{ clickable: isRowClickable }"
          >
            <td v-for="c in headers" :key="c.key" class="table-td" :style="{ textAlign: c.align || 'left' }">
              <!-- Scoped slot for custom rendering, fallback to default display if slot is not provided -->
              <slot :name="`cell-${c.key}`" :item="item" :index="listStart + idx + 1">
                <!-- Default cell fallbacks -->
                <template v-if="c.key === 'index'">
                  <span class="cell-text">{{ listStart + idx + 1 }}</span>
                </template>
                <template v-else-if="c.key === 'materials'">
                  <span class="cell-text">
                    {{ item.materials?.join(', ') ?? '—' }}
                  </span>
                </template>
                <template v-else>
                  <span class="cell-text">
                    {{ item[c.key] ?? '—' }}
                  </span>
                </template>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-container">
      <button
        class="nav-btn"
        :disabled="pinnedPage === 1"
        @click="pinnedPage > 1 && pinnedPage--"
      >
        <ChevronLeft :size="13" :stroke-width="2.5" style="color: #6B7280;" />
      </button>
      <template v-for="(p, i) in paginationPages" :key="i">
        <span v-if="p === '...'" class="pagination-ellipsis">…</span>
        <button
          v-else
          @click="handlePageClick(p)"
          class="page-btn"
          :class="{ active: pinnedPage === p }"
        >{{ p }}</button>
      </template>
      <button
        class="nav-btn"
        :disabled="pinnedPage === totalPages"
        @click="pinnedPage < totalPages && pinnedPage++"
      >
        <ChevronRight :size="13" :stroke-width="2.5" style="color: #6B7280;" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.table-container {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.header-row {
  background-color: #F8FAFC;
  border-bottom: 1px solid #E5E7EB;
}

.table-th {
  padding: 9px 12px;
  font-size: 10px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.07em;
  white-space: nowrap;
  user-select: none;
  transition: color 0.15s ease;
}

.table-th.active {
  color: #6366F1;
}

.th-label-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.sort-icon-wrapper {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  transition: opacity 0.15s ease;
}

.table-td {
  padding: 10px 12px;
}

.cell-text {
  font-size: 12px;
  color: #374151;
  font-weight: 500;
}

.data-row {
  transition: background-color 0.15s ease;
}

.data-row.clickable {
  cursor: pointer;
}

.data-row.clickable:hover {
  background-color: #FAFBFF !important;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 14px;
}

.pagination-ellipsis {
  font-size: 12px;
  color: #9CA3AF;
  padding: 0 2px;
}

.page-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.page-btn:hover {
  background-color: #F9FAFB;
}

.page-btn.active {
  border-color: #0D1B3E;
  background-color: #0D1B3E;
  color: #ffffff;
  font-weight: 700;
}

.nav-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.nav-btn:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.nav-btn:not(:disabled):hover {
  background-color: #F9FAFB;
}

.empty-state-td {
  padding: 40px 24px;
  text-align: center;
}

.empty-state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.empty-state-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  margin-bottom: 4px;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.empty-state-desc {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.5;
  margin: 0;
}
</style>
